<script lang="ts" setup generic="T">

import 'vue-multiselect/dist/vue-multiselect.css';

import Multiselect from 'vue-multiselect';

import { ref, watch } from '#imports';

const props = withDefaults(
    defineProps<{
        searchable?: boolean;
        allowEmpty?: boolean;
        options: T[];
        trackBy?: string;
        error?: string;
        label?: string;
        isDisabled?: boolean;
        initialValue?: T;
        containerClass?: string;
    }>(),
    {
        searchable: false,
        allowEmpty: false,
        isDisabled: false,
        error: undefined,
        initialValue: undefined,
        trackBy: undefined,
        label: undefined,
        containerClass: undefined
    }
);

const emit = defineEmits<{ (e: 'onChange', payload: T): void }>();

defineOptions({
    inheritAttrs: false
});

const value = ref<T | undefined>(props?.initialValue);

watch(
    value,
    newValue => emit(
        'onChange',
        newValue as T
    )
);
</script>

<template>
  <div :class="[{ 'is-disabled': isDisabled }, containerClass]"
       class="position-relative">
    <Multiselect v-model="value"
                 :searchable="searchable"
                 :allow-empty="allowEmpty"
                 :options="options"
                 :track-by="trackBy"
                 class="multiselect"
                 v-bind="$attrs"
                 :label="label"
                 :show-labels="false"
                 :aria-label="isDisabled ? `option: ${value.label}`: 'choose an option'"
    >
      <template #caret="{ toggle }">
        <IconMain class="multiselect__select"
                  icon="chevron-down"
                  :size="25"
                  @mousedown.prevent.stop="toggle"
        />
      </template>
    </Multiselect>
    <Transition name="fade"
                mode="out-in"
    >
      <span v-if="error"
            class="error compact-300-accent"
      >
        {{ error }}
      </span>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
.is-disabled {
  pointer-events: none;

  .multiselect__select {
    display: none;
  }
}

:deep(.multiselect) {
  outline: none !important;
  color: inherit;
  cursor: pointer;
}

:deep(.multiselect__placeholder) {
  margin-bottom: 0;
  padding-top: 0;
  font-size: 16px;
}

:deep(.multiselect--active .multiselect__placeholder) {
  display: block;
}

:deep(.multiselect__tags) {
  padding: 8px 30px 0 8px;
  border-radius: var(--control-400-radius, 2px);
  border-color: var(--bg-secondary);
  background-color: var(--bg-secondary);
}

:deep(.multiselect__select) {
  width: 30px;
  padding: 2px 4px;
}

:deep(.multiselect--disabled) {
  pointer-events: none;

  .multiselect__select {
    background: transparent;
    color: inherit;
  }
}

:deep(.multiselect__single) {
  margin-bottom: 0;
  padding-left: 0;
  background-color: var(--bg-secondary);
  color: var(--content-primary);
}

:deep(.multiselect__content-wrapper) {
  border-bottom-right-radius: var(--control-400-radius, 2px);
  border-bottom-left-radius: var(--control-400-radius, 2px);
  border-color: var(--bg-secondary);
  background-color: var(--bg-secondary);
}

:deep(.multiselect--above .multiselect__content-wrapper) {
  border-top: none;
}

:deep(.multiselect__option) {
  transition: filter 0.3s ease;
  background-color: var(--bg-secondary);
  color: var(--content-primary);
}

:deep(.multiselect__option--highlight) {
  filter: brightness(1.2);
}

:deep(.multiselect__option--selected) {
  background-color: var(--bg-secondary);
  font-weight: normal;
  filter: brightness(1.4);
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background-color: var(--bg-secondary);
  filter: brightness(1.4);
}

.error {
  position: absolute;
  bottom: -18px;
  color: var(--content-negative);
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
